import React from "react";
import { Alert, Button, Card, CardBody, Col, Input, Row, Table } from "reactstrap";
import { DETALLE_TARJETA_PROVEEDORES } from "../constants";
import { FormaPagoDisponible } from "../types";

interface PagosStepProps {
  formasPago: FormaPagoDisponible[];
  pagosRegistrados: Record<number, string>;
  notasFormasPago: Record<number, string>;
  errorFormasPago: string | null;
  cargandoFormasPago: boolean;
  totalFormasPago: number;
  onMontoChange: (id: number, valor: string) => void;
  onNotaChange: (id: number, valor: string) => void;
  onAbrirEfectivo: (forma: FormaPagoDisponible) => void;
  onAbrirTarjeta: (forma: FormaPagoDisponible, tipo: "credito" | "debito") => void;
  detalleEfectivo: Record<number, Record<number, string>>;
  detalleTarjeta: Record<number, Record<string, string>>;
}

const PagosStep = ({
  formasPago,
  pagosRegistrados,
  notasFormasPago,
  errorFormasPago,
  cargandoFormasPago,
  totalFormasPago,
  onMontoChange,
  onNotaChange,
  onAbrirEfectivo,
  onAbrirTarjeta,
  detalleEfectivo,
  detalleTarjeta,
}: PagosStepProps) => {
  const normalizarDescripcion = (texto: string) =>
    texto ? texto.normalize("NFD").replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase() : "";

  const obtenerTipoTarjeta = (descripcion: string): "credito" | "debito" | null => {
    const texto = normalizarDescripcion(descripcion);
    if (texto.includes("CREDITO")) {
      return "credito";
    }
    if (texto.includes("DEBITO")) {
      return "debito";
    }
    return null;
  };

  const esFormaEfectivo = (descripcion: string) => normalizarDescripcion(descripcion).includes("EFECTIVO");

  const crearResumenTarjeta = (formaId: number) => {
    const detalle = detalleTarjeta[formaId] ?? {};
    const partes = DETALLE_TARJETA_PROVEEDORES.filter((proveedor) => Number(detalle[proveedor] ?? "0") > 0).map(
      (proveedor) => `${proveedor}: Gs. ${Number(detalle[proveedor] ?? "0").toLocaleString("es-PY")}`,
    );
    if (!partes.length) {
      return null;
    }
    return partes.join(" • ");
  };

  const crearResumenEfectivo = (formaId: number) => {
    const detalle = detalleEfectivo[formaId] ?? {};
    const total = Object.entries(detalle).reduce((acc, [denom, cantidad]) => {
      const denominacion = Number(denom);
      return acc + denominacion * Number(cantidad ?? "0");
    }, 0);
    if (!total) {
      return null;
    }
    return `Último total cargado: Gs. ${total.toLocaleString("es-PY")}`;
  };

  const renderBotonMonto = (
    forma: FormaPagoDisponible,
    valor: number,
    esEfectivo: boolean,
    tipoTarjeta: "credito" | "debito" | null,
  ) => {
    const etiqueta = valor > 0 ? `Gs. ${valor.toLocaleString("es-PY")}` : "Cargar detalle";
    if (esEfectivo) {
      return (
        <Button
          color={valor > 0 ? "success" : "secondary"}
          outline
          size="sm"
          className="w-100 d-flex justify-content-between align-items-center"
          onClick={() => onAbrirEfectivo(forma)}
        >
          <span>Detallar efectivo</span>
          <span className="fw-semibold">{etiqueta}</span>
        </Button>
      );
    }
    if (tipoTarjeta) {
      return (
        <Button
          color={valor > 0 ? "primary" : "secondary"}
          outline
          size="sm"
          className="w-100 d-flex justify-content-between align-items-center"
          onClick={() => onAbrirTarjeta(forma, tipoTarjeta)}
        >
          <span>{tipoTarjeta === "credito" ? "Detalle crédito" : "Detalle débito"}</span>
          <span className="fw-semibold">{etiqueta}</span>
        </Button>
      );
    }
    return (
      <Input
        type="number"
        min="0"
        value={pagosRegistrados[forma.id] ?? ""}
        onChange={(event) => onMontoChange(forma.id, event.target.value)}
      />
    );
  };

  return (
    <Row>
      <Col>
        <Card>
          <CardBody className="pt-0">
          {errorFormasPago && <Alert color="danger">{errorFormasPago}</Alert>}
          {cargandoFormasPago ? (
            <p className="text-muted mb-0">Cargando formas de pago...</p>
          ) : formasPago.length === 0 ? (
            <p className="text-muted mb-0">No se encontraron formas de pago activas.</p>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle table-sm table-borderless mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Forma</th>
                    <th className="text-end" style={{ width: 140 }}>
                      Monto (Gs)
                    </th>
                    <th style={{ minWidth: 220 }}>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {formasPago.map((forma) => {
                    const tipoTarjeta = obtenerTipoTarjeta(forma.descripcion ?? "");
                    const esEfectivo = esFormaEfectivo(forma.descripcion ?? "");
                    const valor = Number(pagosRegistrados[forma.id] ?? "0");
                    const resumenEfectivo = esEfectivo ? crearResumenEfectivo(forma.id) : null;
                    const resumenTarjeta = tipoTarjeta ? crearResumenTarjeta(forma.id) : null;
                    return (
                      <tr key={forma.id}>
                        <td className="fw-semibold align-top">
                          {forma.descripcion}
                          {(esEfectivo || tipoTarjeta) && (
                            <small className="text-muted d-block">
                              {esEfectivo
                                ? "Usá el botón para detallar billetes."
                                : "Cargá el detalle por proveedor."}
                            </small>
                          )}
                          {resumenEfectivo && <small className="text-muted d-block">{resumenEfectivo}</small>}
                          {resumenTarjeta && <small className="text-muted d-block">{resumenTarjeta}</small>}
                        </td>
                        <td className="text-end" style={{ width: 200 }}>
                          {renderBotonMonto(forma, valor, esEfectivo, tipoTarjeta)}
                        </td>
                        <td className="align-top">
                          <Input
                            type="textarea"
                            rows={1}
                            className="py-1"
                            value={notasFormasPago[forma.id] ?? ""}
                            onChange={(event) => onNotaChange(forma.id, event.target.value)}
                            placeholder="Observaciones opcionales"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
          <div className="d-flex justify-content-end mt-3">
            <div className="border rounded px-3 py-2 text-end">
              <div className="text-muted small">Total declarado</div>
              <div className="h5 mb-0">Gs. {totalFormasPago.toLocaleString("es-PY")}</div>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  </Row>
  );
};

export default PagosStep;
